// API Client for fetching garden data

import { config } from './config'
import { getAuthHeader } from './jwt'
import { debug } from '@/utils/debug'
import type {
  Garden,
  Plant,
  Building,
  GroundShape,
  JsonApiResponse,
  JsonApiListResponse,
  JsonApiResource,
  Product,
  ProductFile,
  Point,
  Layer,
  ProductCategory,
  ProductSearchParams,
  ProductSearchResult,
  CreateGardenObjectPayload,
  UpdateGardenObjectPayload
} from '@/types'

const DEFAULT_LOCALE = 'pl'

class ApiClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = config.serviceUrl
    debug.log('API', `API Client initialized with baseUrl: ${this.baseUrl}`)
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const authHeader = getAuthHeader()
    if (!authHeader) {
      debug.error('API', 'No auth header available')
      throw new Error('Not authenticated')
    }

    const url = `${this.baseUrl}/${path}${path.includes('?') ? '&' : '?'}locale=${DEFAULT_LOCALE}`
    const method = options.method || 'GET'
    debug.log('API', `Request: ${method} ${url}`)

    const startTime = Date.now()
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
        ...options.headers
      }
    })

    const elapsed = Date.now() - startTime
    debug.log('API', `Response: ${response.status} ${response.statusText} (${elapsed}ms)`)

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'No body')
      debug.error('API', `Request failed: ${response.status}`, errorText)
      throw new Error(`API error: ${response.status} ${response.statusText}`)
    }

    // Handle 204 No Content (DELETE responses)
    if (response.status === 204) {
      return {} as T
    }

    const data = await response.json()
    debug.log('API', `Response data keys:`, Object.keys(data))
    return data
  }

  /**
   * Fetch list of user's gardens
   */
  async getGardens(): Promise<{ id: string; name: string; thumbnailUrl?: string }[]> {
    debug.log('API', 'Fetching gardens list...')
    const response = await this.request<JsonApiListResponse>('gardens')
    const gardens = response.data.map(item => ({
      id: item.id,
      name: item.attributes['name'] as string,
      thumbnailUrl: item.attributes['preview-thumbnail-url'] as string | undefined
    }))
    debug.success('API', `Fetched ${gardens.length} gardens`)
    return gardens
  }

  /**
   * Fetch a single garden with all its objects
   */
  async getGarden(id: string): Promise<Garden> {
    debug.log('API', `Fetching garden ${id}...`)

    // Get garden basic info
    const gardenResponse = await this.request<JsonApiResponse>(`gardens/${id}`)
    const objectsData = gardenResponse.data?.relationships?.objects?.data
    const objectCount = Array.isArray(objectsData) ? objectsData.length : (objectsData ? 1 : 0)
    debug.log('API', 'Garden response received', {
      id: gardenResponse.data?.id,
      objectCount
    })

    // Parse garden metadata (objects loaded separately via getGardenWithAssets)
    const garden = this.parseGardenMetadata(gardenResponse)

    debug.success('API', `Parsed garden: "${garden.name}"`)

    return garden
  }

  /**
   * Fetch garden objects with products and textures in a single API call
   * Uses include parameter to get all data at once
   * @param categoryLayerMap - Map of category IDs to layers for object classification
   */
  async getGardenWithAssets(
    gardenId: string,
    options: { textureKinds?: string[]; categoryLayerMap?: Map<string, Layer> } = {}
  ): Promise<{
    plants: Plant[]
    buildings: Building[]
    groundShapes: GroundShape[]
    products: Map<string, Product>
    modelUrls: Map<string, ProductFile[]>
    textureUrls: Map<string, ProductFile[]>
  }> {
    debug.log('API', `Fetching garden objects with assets for garden ${gardenId}...`)

    // Build query params
    const params = new URLSearchParams()
    params.set('garden_id', gardenId)
    params.append('include[]', 'product.textures')

    // Filter texture kinds (glb for models, top for ground textures, icon for thumbnails)
    const textureKinds = options.textureKinds || ['glb', 'top', 'icon']
    for (const kind of textureKinds) {
      params.append('filter[texture_kinds][]', kind)
    }

    const response = await this.request<JsonApiListResponse>(`garden_objects?${params.toString()}`)

    debug.log('API', `Received ${response.data?.length || 0} garden objects, ${response.included?.length || 0} included resources`)

    // Parse included resources first (products and textures)
    const { products, textures } = this.parseIncludedResources(response.included || [])

    // Use provided category map or create empty one (will use fallback logic)
    const categoryLayerMap = options.categoryLayerMap || new Map<string, Layer>()
    if (!options.categoryLayerMap) {
      debug.warn('API', 'No categoryLayerMap provided, using geometry-based fallback')
    }

    // Parse garden objects using category-based classification
    const plants: Plant[] = []
    const buildings: Building[] = []
    const groundShapes: GroundShape[] = []

    for (const item of response.data || []) {
      const parsed = this.parseGardenObject(item, products, categoryLayerMap)
      if (parsed) {
        if (parsed.type === 'plant') {
          plants.push(parsed as Plant)
        } else if (parsed.type === 'building') {
          buildings.push(parsed as Building)
        } else if (['rectangle', 'polygon', 'ellipse', 'path'].includes(parsed.type)) {
          groundShapes.push(parsed as GroundShape)
        }
      }
    }

    // Organize textures by product and kind
    // Note: Side-view billboards (kind: billboard/null) are fetched separately via
    // getBillboardTexture() which supports month/size filters
    const modelUrls = new Map<string, ProductFile[]>()
    const textureUrls = new Map<string, ProductFile[]>()

    for (const [productId, productTextures] of textures) {
      const glbFiles = productTextures.filter(t => t.kind === 'glb')
      const topFiles = productTextures.filter(t => t.kind === 'top')
      const iconFiles = productTextures.filter(t => t.kind === 'icon')

      if (glbFiles.length > 0) {
        modelUrls.set(productId, glbFiles)
      }
      if (topFiles.length > 0) {
        textureUrls.set(productId, topFiles)
      }
      // Set icon URL on product for catalog thumbnails
      if (iconFiles.length > 0) {
        const product = products.get(productId)
        if (product && !product.iconUrl) {
          product.iconUrl = iconFiles[0].fileUrl
        }
      }
    }

    // Log parsed objects with their available assets
    debug.log('API', '--- Parsed Objects ---')

    const allObjects = [
      ...plants.map(p => ({ obj: p, layer: 'plant' as const })),
      ...buildings.map(b => ({ obj: b, layer: 'building' as const })),
      ...groundShapes.map(s => ({ obj: s, layer: 'ground' as const }))
    ]

    for (const { obj, layer } of allObjects) {
      const product = obj.productId ? products.get(obj.productId) : undefined
      const productCode = product?.code || '?'
      const hasGlb = obj.productId ? modelUrls.has(obj.productId) : false
      const hasTop = obj.productId ? textureUrls.has(obj.productId) : false

      // Build assets string
      const assets: string[] = []
      if (hasGlb) assets.push('glb')
      if (hasTop) assets.push('top')
      const assetsStr = assets.length > 0 ? `[${assets.join(', ')}]` : '[no textures]'

      if (layer === 'ground') {
        const shape = obj as GroundShape
        debug.log('API', `  ${layer}: ${shape.type} ${shape.width}x${shape.height} @ (${obj.x}, ${obj.y}) ${assetsStr}`)
      } else {
        debug.log('API', `  ${layer}: ${productCode} @ (${obj.x}, ${obj.y}) ${assetsStr}`)
      }
    }

    debug.success('API', `Summary: ${plants.length} plants, ${buildings.length} buildings, ${groundShapes.length} ground shapes`)
    debug.success('API', `Assets: ${modelUrls.size} glb, ${textureUrls.size} top`)

    return { plants, buildings, groundShapes, products, modelUrls, textureUrls }
  }

  /**
   * Parse included resources from JSON:API response
   * Extracts products and textures from the included array
   */
  private parseIncludedResources(included: JsonApiResource[]): {
    products: Map<string, Product>
    textures: Map<string, ProductFile[]>
  } {
    const products = new Map<string, Product>()
    const textures = new Map<string, ProductFile[]>()

    for (const item of included) {
      if (item.type === 'products') {
        const attrs = item.attributes
        const product: Product = {
          id: item.id,
          code: attrs['code'] as string || `ID-${item.id}`,
          name: attrs['name'] as string || 'Unknown',
          latinName: attrs['latin-name'] as string | undefined,
          mainCategoryId: attrs['main-category-id'] != null ? String(attrs['main-category-id']) : undefined,
          elevation: attrs['elevation'] as number | undefined,
          width: attrs['width'] as number | undefined,
          height: attrs['height'] as number | undefined
        }
        products.set(item.id, product)
      } else if (item.type === 'textures') {
        const attrs = item.attributes
        // Get product ID from relationship
        const productRelation = item.relationships?.product?.data as { id: string } | undefined
        const productId = productRelation?.id || attrs['product-id'] as string

        if (!productId) continue

        const file: ProductFile = {
          id: item.id,
          productId,
          kind: attrs['kind'] as ProductFile['kind'],
          fileUrl: attrs['file-url'] as string,
          month: attrs['season'] as number | undefined,
          size: attrs['size'] as string | undefined,
          dpm: attrs['dpm'] as number | undefined,
          width: attrs['width'] as number | undefined,
          height: attrs['height'] as number | undefined
        }

        if (!textures.has(productId)) {
          textures.set(productId, [])
        }
        textures.get(productId)!.push(file)
      }
    }

    return { products, textures }
  }

  /**
   * Fetch billboard texture for a specific product
   * Used as fallback when no GLB model is available
   * @param productId - Product ID to fetch billboard for
   * @param options - Optional month (default 7 for July), size (default 2 for mature), dpm (default 100 for HD)
   * @returns ProductFile with billboard texture or null if not found
   */
  async getBillboardTexture(
    productId: string,
    options: { month?: number; size?: number; dpm?: number } = {}
  ): Promise<ProductFile | null> {
    const { month = 7, size = 2, dpm = 100 } = options
    debug.log('API', `Fetching billboard texture for product ${productId} (month=${month}, size=${size}, dpm=${dpm})...`)

    const params = new URLSearchParams()
    params.append('filter[product_ids][]', productId)
    params.append('filter[kinds][]', 'billboard')
    params.set('filter[month]', month.toString())
    params.set('filter[size]', size.toString())
    params.set('filter[dpm]', dpm.toString())

    try {
      const response = await this.request<JsonApiListResponse>(`textures?${params.toString()}`)

      if (!response.data || response.data.length === 0) {
        debug.log('API', `No billboard texture found for product ${productId}`)
        return null
      }

      const item = response.data[0]
      const attrs = item.attributes
      const productRelation = item.relationships?.product?.data as { id: string } | undefined

      const file: ProductFile = {
        id: item.id,
        productId: productRelation?.id || productId,
        kind: 'billboard' as ProductFile['kind'],
        fileUrl: attrs['file-url'] as string,
        dpm: attrs['dpm'] as number | undefined,
        width: attrs['width'] as number | undefined,
        height: attrs['height'] as number | undefined
      }

      debug.success('API', `Billboard texture found: ${file.fileUrl} (${file.width}x${file.height} dots)`)
      return file
    } catch (error) {
      debug.warn('API', `Failed to fetch billboard texture for product ${productId}`, error)
      return null
    }
  }

  /**
   * Fetch textures (GLB models, images) for rendering
   * Uses the /textures endpoint per API spec
   */
  async getTextures(
    productIds: string[],
    options: { month?: number; size?: number; kinds?: string[] } = {}
  ): Promise<Map<string, ProductFile[]>> {
    if (productIds.length === 0) {
      debug.log('API', 'No product IDs to fetch')
      return new Map()
    }

    debug.log('API', `Fetching textures for ${productIds.length} products...`)

    // Build params using array notation like the legacy editor
    const params = new URLSearchParams()

    // Each product ID as separate array element: filter[product_ids][]=123
    for (const id of productIds) {
      params.append('filter[product_ids][]', id)
    }

    // Kinds as array: filter[kinds][]=glb
    const kinds = options.kinds || ['glb']
    for (const kind of kinds) {
      params.append('filter[kinds][]', kind)
    }

    // Month and size as regular params
    if (options.month) params.set('filter[month]', options.month.toString())
    if (options.size) params.set('filter[size]', options.size.toString())

    try {
      const response = await this.request<JsonApiListResponse>(
        `textures?${params.toString()}`
      )

      const filesByProduct = new Map<string, ProductFile[]>()
      for (const item of response.data) {
        // Get product ID from relationship
        const productRelation = item.relationships?.product?.data as { id: string } | undefined
        const productId = productRelation?.id || item.attributes['product-id'] as string

        if (!productId) continue

        const file: ProductFile = {
          id: item.id,
          productId,
          kind: item.attributes['kind'] as ProductFile['kind'],
          fileUrl: item.attributes['file-url'] as string,
          month: item.attributes['season'] as number | undefined,
          size: item.attributes['size'] as string | undefined,
          width: item.attributes['width'] as number | undefined,
          height: item.attributes['height'] as number | undefined
        }

        if (!filesByProduct.has(productId)) {
          filesByProduct.set(productId, [])
        }
        filesByProduct.get(productId)!.push(file)
      }

      debug.success('API', `Fetched textures for ${filesByProduct.size} products`)
      return filesByProduct
    } catch (error) {
      debug.warn('API', 'Failed to fetch textures, continuing without 3D models', error)
      return new Map()
    }
  }

  /**
   * Fetch product details for given product IDs
   * Returns a map of productId -> Product
   */
  async getProducts(productIds: string[]): Promise<Map<string, Product>> {
    if (productIds.length === 0) {
      debug.log('API', 'No product IDs to fetch')
      return new Map()
    }

    debug.log('API', `Fetching ${productIds.length} products...`)

    // Build params using array notation
    const params = new URLSearchParams()
    for (const id of productIds) {
      params.append('filter[ids][]', id)
    }

    try {
      const response = await this.request<JsonApiListResponse>(
        `products?${params.toString()}`
      )

      const productsMap = new Map<string, Product>()
      for (const item of response.data) {
        const attrs = item.attributes
        const product: Product = {
          id: item.id,
          code: attrs['code'] as string || `ID-${item.id}`,
          name: attrs['name'] as string || 'Unknown',
          latinName: attrs['latin-name'] as string | undefined,
          mainCategoryId: attrs['main-category-id'] != null ? String(attrs['main-category-id']) : undefined,
          elevation: attrs['elevation'] as number | undefined,
          width: attrs['width'] as number | undefined,
          height: attrs['height'] as number | undefined
        }
        productsMap.set(item.id, product)
        debug.log('API', `  Product ${product.code}: ${product.name} (${product.width}x${product.height})`)
      }

      debug.success('API', `Fetched ${productsMap.size} products`)
      return productsMap
    } catch (error) {
      debug.warn('API', 'Failed to fetch products', error)
      return new Map()
    }
  }

  /**
   * Fetch product categories for a specific layer
   * GET /product_categories?layer={layer}
   */
  async getCategories(layer: Layer): Promise<ProductCategory[]> {
    // Map internal layer names to API parameter values
    const apiLayerParam = layer === 'architecture' ? 'buildings' : layer
    debug.log('API', `Fetching categories for layer: ${layer} (API param: ${apiLayerParam})`)

    try {
      const response = await this.request<JsonApiListResponse>(
        `product_categories?layer=${apiLayerParam}`
      )

      const categories: ProductCategory[] = response.data.map(item => ({
        id: item.id,
        name: item.attributes['name'] as string,
        layer: layer,
        parentId: item.attributes['parent-id'] as string | undefined
      }))

      debug.success('API', `Fetched ${categories.length} categories for ${layer}`)
      debug.log('API', `Category IDs for ${layer}: ${categories.map(c => c.id).join(', ')}`)
      return categories
    } catch (error) {
      debug.warn('API', `Failed to fetch categories for ${layer}`, error)
      return []
    }
  }

  /**
   * Fetch categories for all layers and build a lookup map
   * Returns: Map<categoryId, Layer>
   */
  async getCategoryLayerMap(): Promise<Map<string, Layer>> {
    debug.log('API', 'Building category layer map...')

    const layers: Layer[] = ['ground', 'architecture', 'plants']
    const categoryMap = new Map<string, Layer>()

    // Fetch categories for all layers in parallel
    const results = await Promise.all(
      layers.map(layer => this.getCategories(layer))
    )

    // Build the lookup map
    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i]
      const categories = results[i]
      for (const category of categories) {
        categoryMap.set(category.id, layer)
      }
    }

    debug.success('API', `Category map built with ${categoryMap.size} categories`)
    return categoryMap
  }

  /**
   * Parse garden metadata from API response (without objects)
   */
  private parseGardenMetadata(gardenResponse: JsonApiResponse): Garden {
    const data = gardenResponse.data
    const attrs = data.attributes

    debug.log('API', 'Garden attributes:', Object.keys(attrs))

    // Parse scheme outline if present
    let schemeOutline = undefined
    const outlineGeometry = attrs['outline-geometry'] as string | null
    if (outlineGeometry && outlineGeometry.trim() !== '') {
      schemeOutline = this.parseOutlineGeometry(outlineGeometry)
      debug.log('API', 'Parsed scheme outline', schemeOutline ? 'success' : 'failed')
    }

    return {
      id: data.id,
      token: attrs['token'] as string,
      name: attrs['name'] as string,
      description: attrs['description'] as string | undefined,
      rotation: (attrs['rotation'] as number) || 0,
      isEditable: attrs['is-editable'] as boolean,
      previewThumbnailUrl: attrs['preview-thumbnail-url'] as string | undefined,

      schemeImage: {
        imageUrl: attrs['background-image-url'] as string | undefined,
        x: (attrs['background-offset-x'] as number) || 0,
        y: (attrs['background-offset-y'] as number) || 0,
        scale: (attrs['background-scale'] as number) || 1,
        // Fall back to garden rotation — server may not store background-rotation as a separate attr
        rotation: (attrs['background-rotation'] as number) || (attrs['rotation'] as number) || undefined,
      },
      schemeOutline,

      // Flexible key-value store
      properties: attrs['properties'] as Record<string, unknown> | undefined,

      // Objects will be populated by getGardenWithAssets
      plants: [],
      buildings: [],
      groundShapes: []
    }
  }

  /**
   * Parse a garden object from the API response
   * Determines type based on product's category layer
   */
  private parseGardenObject(
    item: JsonApiResource,
    products: Map<string, Product>,
    categoryLayerMap: Map<string, Layer>
  ): Plant | Building | GroundShape | null {
    const attrs = item.attributes

    // Get product relationship to determine object type
    const productRelation = item.relationships?.product?.data as { id: string; type: string } | undefined
    const productId = productRelation?.id

    // Check if it has geometry (ground shape) - SVG format like <rect>, <polygon>, <ellipsis>
    const geometry = attrs['geometry'] as string | null
    const hasGeometry = geometry && geometry.trim() !== '' && geometry !== '[]'

    // Get product for elevation fallback and layer detection
    const product = productId ? products.get(productId) : undefined

    // Use garden object elevation, falling back to product elevation
    // This is important for architecture items (planters, decks) that define elevation in the product
    const objectElevation = attrs['elevation'] as number | null
    const effectiveElevation = (objectElevation != null && objectElevation > 0)
      ? objectElevation
      : (product?.elevation ?? 0)

    const baseObject = {
      id: item.id,
      x: (attrs['x'] as number) || 0,
      y: (attrs['y'] as number) || 0,
      z: (attrs['z'] as number) || 0,
      rotation: (attrs['rotation'] as number) || 0,
      elevation: effectiveElevation,
      zIndex: (attrs['z-index'] as number) || 0,
      productId: productId,
      productName: product?.name,
      label: attrs['label'] as string | undefined
    }

    // Determine layer from product's main category
    let layer: Layer | undefined
    if (product?.mainCategoryId) {
      layer = categoryLayerMap.get(product.mainCategoryId)
    }

    // Fallback: use geometry presence to determine layer
    if (!layer) {
      layer = hasGeometry ? 'ground' : 'plants'
    }

    // Parse based on layer
    switch (layer) {
      case 'ground':
        return this.parseAsGroundShape(item, baseObject, geometry)

      case 'architecture':
        return this.parseAsBuilding(item, baseObject, product)

      case 'plants':
      default:
        return this.parseAsPlant(item, baseObject, product)
    }
  }

  /**
   * Parse object as a ground shape
   */
  private parseAsGroundShape(
    item: JsonApiResource,
    baseObject: Omit<Plant, 'type' | 'planted' | 'width' | 'height'>,
    geometry: string | null
  ): GroundShape | null {
    // Ground shapes need geometry for dimensions
    if (geometry && geometry.trim() !== '' && geometry !== '[]') {
      const parsed = this.parseSvgGeometry(geometry)
      if (parsed) {
        return {
          ...baseObject,
          type: parsed.type,
          width: parsed.width,
          height: parsed.height,
          points: parsed.points,
          pathWaypoints: parsed.pathWaypoints,
          pathWidth: parsed.pathWidth,
          textureUrl: undefined
        } as GroundShape
      }
    }

    // If no valid geometry, use default rectangle
    const attrs = item.attributes
    return {
      ...baseObject,
      type: 'rectangle',
      width: (attrs['width'] as number) || 100,
      height: (attrs['height'] as number) || 100,
      textureUrl: undefined
    } as GroundShape
  }

  /**
   * Parse object as a building
   */
  private parseAsBuilding(
    item: JsonApiResource,
    baseObject: Omit<Plant, 'type' | 'planted' | 'width' | 'height'>,
    product?: Product
  ): Building {
    const attrs = item.attributes
    return {
      ...baseObject,
      type: 'building',
      width: (attrs['width'] as number) || product?.width || 100,
      height: (attrs['height'] as number) || product?.height || 100
    } as Building
  }

  /**
   * Parse object as a plant
   */
  private parseAsPlant(
    item: JsonApiResource,
    baseObject: Omit<Plant, 'type' | 'planted' | 'width' | 'height'>,
    product?: Product
  ): Plant {
    const attrs = item.attributes
    return {
      ...baseObject,
      type: 'plant',
      width: (attrs['width'] as number) || product?.width || 50,
      height: (attrs['height'] as number) || product?.height || 50,
      planted: false
    } as Plant
  }

  /**
   * Parse SVG geometry string to extract shape type and dimensions
   * Formats:
   *   <rect width='1000' height='100' />
   *   <polygon points='x1,y1 x2,y2 ...' />
   *   <ellipsis width='500' height='300' />
   */
  private parseSvgGeometry(geometry: string): {
    type: 'rectangle' | 'polygon' | 'ellipse' | 'path'
    width: number
    height: number
    points?: Point[]
    pathWaypoints?: Point[]
    pathWidth?: number
  } | null {
    // Decode HTML entities if present
    const decoded = geometry
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")

    // Check for rectangle: <rect width='...' height='...' />
    const rectMatch = decoded.match(/<rect[^>]*width=['"]([\d.]+)['"][^>]*height=['"]([\d.]+)['"]/)
    if (rectMatch) {
      return {
        type: 'rectangle',
        width: parseFloat(rectMatch[1]),
        height: parseFloat(rectMatch[2])
      }
    }

    // Also try height before width
    const rectMatch2 = decoded.match(/<rect[^>]*height=['"]([\d.]+)['"][^>]*width=['"]([\d.]+)['"]/)
    if (rectMatch2) {
      return {
        type: 'rectangle',
        width: parseFloat(rectMatch2[2]),
        height: parseFloat(rectMatch2[1])
      }
    }

    // Check for ellipse: <ellipsis width='...' height='...' /> (note: legacy uses "ellipsis")
    const ellipseMatch = decoded.match(/<ellips[ie]s?[^>]*width=['"]([\d.]+)['"][^>]*height=['"]([\d.]+)['"]/)
    if (ellipseMatch) {
      return {
        type: 'ellipse',
        width: parseFloat(ellipseMatch[1]),
        height: parseFloat(ellipseMatch[2])
      }
    }

    // Check for path: <path waypoints='...' width='...' outline='...' />
    const pathMatch = decoded.match(/<path[^>]*waypoints=['"]([^'"]+)['"][^>]*width=['"]([\d.]+)['"][^>]*outline=['"]([^'"]+)['"]/)
    if (pathMatch) {
      const waypointsStr = pathMatch[1]
      const pathWidth = parseFloat(pathMatch[2])
      const outlineStr = pathMatch[3]

      const pathWaypoints: Point[] = waypointsStr.split(/\s+/).map(pair => {
        const [x, y] = pair.split(',').map(parseFloat)
        return { x: x || 0, y: y || 0 }
      }).filter(p => !isNaN(p.x) && !isNaN(p.y))

      const points: Point[] = outlineStr.split(/\s+/).map(pair => {
        const [x, y] = pair.split(',').map(parseFloat)
        return { x: x || 0, y: y || 0 }
      }).filter(p => !isNaN(p.x) && !isNaN(p.y))

      if (pathWaypoints.length >= 2 && points.length >= 3) {
        const xs = points.map(p => p.x)
        const ys = points.map(p => p.y)
        const width = Math.max(...xs) - Math.min(...xs)
        const height = Math.max(...ys) - Math.min(...ys)

        return {
          type: 'path',
          width: width || 50,
          height: height || 50,
          points,
          pathWaypoints,
          pathWidth,
        }
      }
    }

    // Check for polygon: <polygon points='x1,y1 x2,y2 ...' />
    const polygonMatch = decoded.match(/<polygon[^>]*points=['"]([^'"]+)['"]/)
    if (polygonMatch) {
      const pointsStr = polygonMatch[1]
      const points: Point[] = pointsStr.split(/\s+/).map(pair => {
        const [x, y] = pair.split(',').map(parseFloat)
        return { x: x || 0, y: y || 0 }
      }).filter(p => !isNaN(p.x) && !isNaN(p.y))

      if (points.length >= 3) {
        // Calculate bounding box for width/height
        const xs = points.map(p => p.x)
        const ys = points.map(p => p.y)
        const width = Math.max(...xs) - Math.min(...xs)
        const height = Math.max(...ys) - Math.min(...ys)

        return {
          type: 'polygon',
          width: width || 50,
          height: height || 50,
          points
        }
      }
    }

    debug.warn('API', `Could not parse geometry: ${geometry.substring(0, 100)}`)
    return null
  }

  // ============================================================================
  // Garden Create / Update
  // ============================================================================

  /**
   * Create a new garden
   * POST /gardens
   */
  async createGarden(attrs: { name: string; description?: string }): Promise<{ id: string; name: string }> {
    debug.log('API', 'Creating new garden', attrs)

    const body = {
      data: {
        attributes: {
          name: attrs.name,
          ...(attrs.description ? { description: attrs.description } : {}),
        }
      }
    }

    const response = await this.request<JsonApiResponse>(
      'gardens',
      { method: 'POST', body: JSON.stringify(body) }
    )

    const id = response.data.id
    const name = response.data.attributes['name'] as string
    debug.success('API', `Created garden: ${id} ("${name}")`)
    return { id, name }
  }

  /**
   * Update garden-level properties
   * PATCH /gardens/:id
   */
  async updateGarden(id: string, properties: Record<string, unknown>, gardenAttributes?: Record<string, unknown>): Promise<void> {
    debug.log('API', `Updating garden ${id} properties`, Object.keys(properties))

    // Only include properties when non-empty to avoid overwriting existing data
    const attributes: Record<string, unknown> = {
      ...gardenAttributes,
    }
    if (Object.keys(properties).length > 0) {
      attributes.properties = properties
    }

    const body = {
      data: {
        attributes,
      }
    }

    await this.request<JsonApiResponse>(
      `gardens/${id}`,
      { method: 'PATCH', body: JSON.stringify(body) }
    )

    debug.success('API', `Updated garden ${id} properties`)
  }

  // ============================================================================
  // Product Search & Categories
  // ============================================================================

  /**
   * Search products with filters
   * GET /products?category_id=X&filter[layer]=Y&filter[search]=Z&filter[favorite]=true
   */
  async searchProducts(params: ProductSearchParams): Promise<ProductSearchResult> {
    debug.log('API', 'Searching products', params)

    const queryParams = new URLSearchParams()
    if (params.categoryId) queryParams.set('category_id', params.categoryId)
    if (params.layer) {
      // Map internal layer names to API parameter values
      const apiLayer = params.layer === 'architecture' ? 'buildings' : params.layer
      queryParams.set('filter[layer]', apiLayer)
    }
    if (params.search) queryParams.set('filter[search]', params.search)
    if (params.favorite) queryParams.set('filter[favorite]', 'true')
    if (params.page) queryParams.set('page', params.page.toString())
    if (params.perPage) queryParams.set('per_page', params.perPage.toString())

    // Request icon + top textures via include (icons for thumbnails, top for ground surface drawing)
    queryParams.append('include[]', 'textures')
    queryParams.append('filter[texture_kinds][]', 'icon')
    queryParams.append('filter[texture_kinds][]', 'top')

    try {
      const response = await this.request<JsonApiListResponse>(
        `products?${queryParams.toString()}`
      )

      // Parse included textures into maps: productId → URL
      const iconUrls = new Map<string, string>()
      const topTextures = new Map<string, { url: string; width?: number; height?: number }>()
      if (response.included) {
        for (const item of response.included) {
          if (item.type !== 'textures') continue
          const kind = item.attributes['kind'] as string
          const fileUrl = item.attributes['file-url'] as string
          const productRelation = item.relationships?.product?.data as { id: string } | undefined
          const productId = productRelation?.id || item.attributes['product-id'] as string
          if (!productId || !fileUrl) continue

          if (kind === 'icon') {
            iconUrls.set(productId, fileUrl)
          } else if (kind === 'top') {
            topTextures.set(productId, {
              url: fileUrl,
              width: item.attributes['width'] as number | undefined,
              height: item.attributes['height'] as number | undefined,
            })
          }
        }
      }

      const products: Product[] = (response.data || []).map(item => {
        const attrs = item.attributes
        const topTex = topTextures.get(item.id)
        return {
          id: item.id,
          code: attrs['code'] as string || `ID-${item.id}`,
          name: attrs['name'] as string || 'Unknown',
          latinName: attrs['latin-name'] as string | undefined,
          mainCategoryId: attrs['main-category-id'] != null ? String(attrs['main-category-id']) : undefined,
          elevation: attrs['elevation'] as number | undefined,
          width: attrs['width'] as number | undefined,
          height: attrs['height'] as number | undefined,
          // Try icon-url attribute first, then fall back to included texture
          iconUrl: (attrs['icon-url'] as string | undefined) || iconUrls.get(item.id),
          topTextureUrl: topTex?.url,
          topTextureWidth: topTex?.width,
          topTextureHeight: topTex?.height,
        }
      })

      debug.success('API', `Found ${products.length} products (${iconUrls.size} with icons, ${topTextures.size} with top textures)`)
      return {
        products,
        total: response.meta?.total ?? products.length,
        page: response.meta?.page ?? 1,
      }
    } catch (error) {
      debug.warn('API', 'Product search failed', error)
      return { products: [], total: 0, page: 1 }
    }
  }

  /**
   * Get subcategories for a parent category
   * GET /product_categories?parent_id=X
   */
  async getSubcategories(parentId: string): Promise<ProductCategory[]> {
    debug.log('API', `Fetching subcategories for parent: ${parentId}`)

    try {
      const response = await this.request<JsonApiListResponse>(
        `product_categories?parent_id=${parentId}`
      )

      const categories: ProductCategory[] = response.data.map(item => ({
        id: item.id,
        name: item.attributes['name'] as string,
        layer: item.attributes['layer'] as Layer || 'plants',
        parentId: item.attributes['parent-id'] as string | undefined,
      }))

      debug.success('API', `Fetched ${categories.length} subcategories for ${parentId}`)
      return categories
    } catch (error) {
      debug.warn('API', `Failed to fetch subcategories for ${parentId}`, error)
      return []
    }
  }

  /**
   * Duplicate a garden (Save As)
   * POST /gardens/:id/duplicate
   */
  async duplicateGarden(id: string, name: string): Promise<{ id: string; name: string }> {
    debug.log('API', `Duplicating garden ${id} as "${name}"`)

    const body = {
      data: {
        attributes: { name }
      }
    }

    const response = await this.request<JsonApiResponse>(
      `gardens/${id}/duplicate`,
      { method: 'POST', body: JSON.stringify(body) }
    )

    const newId = response.data.id
    const newName = response.data.attributes['name'] as string
    debug.success('API', `Duplicated garden: ${newId} ("${newName}")`)
    return { id: newId, name: newName }
  }

  // ============================================================================
  // Garden Object CRUD
  // ============================================================================

  /**
   * Create a new garden object
   * POST /garden_objects
   */
  async createGardenObject(payload: CreateGardenObjectPayload): Promise<{ id: string }> {
    debug.log('API', 'Creating garden object', payload)

    const attributes: Record<string, unknown> = {
      'x': payload.x,
      'y': payload.y,
      'rotation': payload.rotation ?? 0,
      'elevation': payload.elevation ?? 0,
      'z-index': payload.zIndex ?? 0,
      'garden-id': payload.gardenId,
      'product-id': payload.productId,
    }
    if (payload.geometry) attributes['geometry'] = payload.geometry
    if (payload.label) attributes['label'] = payload.label

    const body = {
      data: { attributes }
    }

    const response = await this.request<JsonApiResponse>(
      'garden_objects',
      { method: 'POST', body: JSON.stringify(body) }
    )

    const id = response.data.id
    debug.success('API', `Created garden object: ${id}`)
    return { id }
  }

  /**
   * Update an existing garden object
   * PATCH /garden_objects/:id
   */
  async updateGardenObject(id: string, payload: UpdateGardenObjectPayload): Promise<void> {
    debug.log('API', `Updating garden object ${id}`, payload)

    const attributes: Record<string, unknown> = {}
    if (payload.x !== undefined) attributes['x'] = payload.x
    if (payload.y !== undefined) attributes['y'] = payload.y
    if (payload.rotation !== undefined) attributes['rotation'] = payload.rotation
    if (payload.elevation !== undefined) attributes['elevation'] = payload.elevation
    if (payload.zIndex !== undefined) attributes['z-index'] = payload.zIndex
    if (payload.geometry !== undefined) attributes['geometry'] = payload.geometry
    if (payload.label !== undefined) attributes['label'] = payload.label
    if (payload.markedForDestruction !== undefined) attributes['marked-for-destruction'] = payload.markedForDestruction

    const body = {
      data: {
        id,
        attributes,
      }
    }

    await this.request<JsonApiResponse>(
      `garden_objects/${id}`,
      { method: 'PATCH', body: JSON.stringify(body) }
    )

    debug.success('API', `Updated garden object: ${id}`)
  }

  /**
   * Delete a garden object
   * DELETE /garden_objects/:id
   */
  async deleteGardenObject(id: string): Promise<void> {
    debug.log('API', `Deleting garden object ${id}`)

    await this.request<Record<string, never>>(
      `garden_objects/${id}`,
      { method: 'DELETE' }
    )

    debug.success('API', `Deleted garden object: ${id}`)
  }

  // ============================================================================
  // Favorites
  // ============================================================================

  /**
   * Add a product to favorites
   * POST /favorite_products/:id
   */
  async addFavorite(productId: string): Promise<void> {
    debug.log('API', `Adding favorite: ${productId}`)
    await this.request<Record<string, never>>(
      `favorite_products/${productId}`,
      { method: 'POST' }
    )
    debug.success('API', `Added favorite: ${productId}`)
  }

  /**
   * Remove a product from favorites
   * DELETE /favorite_products/:id
   */
  async removeFavorite(productId: string): Promise<void> {
    debug.log('API', `Removing favorite: ${productId}`)
    await this.request<Record<string, never>>(
      `favorite_products/${productId}`,
      { method: 'DELETE' }
    )
    debug.success('API', `Removed favorite: ${productId}`)
  }

  // ============================================================================
  // Parsing helpers
  // ============================================================================

  private parseOutlineGeometry(svg: string): { x: number; y: number; points: Point[] } | undefined {
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(svg, 'text/xml')
      const polygon = doc.querySelector('polygon')
      if (!polygon) return undefined

      const x = parseFloat(polygon.getAttribute('data-x') || '0')
      const y = parseFloat(polygon.getAttribute('data-y') || '0')
      const pointsStr = polygon.getAttribute('points') || ''

      const points: Point[] = pointsStr.split(' ').map(pair => {
        const [px, py] = pair.split(',').map(parseFloat)
        return { x: px, y: py }
      }).filter(p => !isNaN(p.x) && !isNaN(p.y))

      return { x, y, points }
    } catch {
      return undefined
    }
  }

}

// Singleton instance
export const api = new ApiClient()
